import {
  GET_STARTED_TASK,
  type GetStartedTaskId,
} from '@/core/guidance/constants';
import { captureEvent, setPersonProperties } from '@/core/posthog-init';

/** Why the user is creating / using a workspace (onboarding intent). */
export const WORKSPACE_INTENT = {
  profiles: 'profiles',
  touchpoints: 'touchpoints',
  reputation: 'reputation',
  full: 'full',
} as const;

export type WorkspaceIntent =
  (typeof WORKSPACE_INTENT)[keyof typeof WORKSPACE_INTENT];

export const WORKSPACE_INTENTS: WorkspaceIntent[] = [
  WORKSPACE_INTENT.profiles,
  WORKSPACE_INTENT.touchpoints,
  WORKSPACE_INTENT.reputation,
  WORKSPACE_INTENT.full,
];

export function isWorkspaceIntent(value: unknown): value is WorkspaceIntent {
  return (
    typeof value === 'string' &&
    (WORKSPACE_INTENTS as readonly string[]).includes(value)
  );
}

/**
 * First-win Loslegen tasks for the chosen onboarding intent.
 * Expansion stays available in the product (nav, Status Board) — it is not
 * a checklist step until the user picks Full ops. Missing intent → full.
 */
const GET_STARTED_TASKS_BY_INTENT: Record<WorkspaceIntent, GetStartedTaskId[]> =
  {
    [WORKSPACE_INTENT.profiles]: [
      GET_STARTED_TASK.meProfile,
      GET_STARTED_TASK.meCard,
      GET_STARTED_TASK.meShare,
      GET_STARTED_TASK.meTour,
    ],
    [WORKSPACE_INTENT.touchpoints]: [
      GET_STARTED_TASK.wsProduct,
      GET_STARTED_TASK.wsLocation,
      GET_STARTED_TASK.meTour,
    ],
    [WORKSPACE_INTENT.reputation]: [
      GET_STARTED_TASK.wsLocation,
      GET_STARTED_TASK.wsGoogle,
      GET_STARTED_TASK.meTour,
    ],
    [WORKSPACE_INTENT.full]: [
      GET_STARTED_TASK.meProfile,
      GET_STARTED_TASK.meCard,
      GET_STARTED_TASK.meTour,
      GET_STARTED_TASK.meShare,
      GET_STARTED_TASK.wsLocation,
      GET_STARTED_TASK.wsProduct,
      GET_STARTED_TASK.wsGoogle,
      GET_STARTED_TASK.wsInvite,
    ],
  };

export function getStartedTaskOrder(
  intent: WorkspaceIntent | null | undefined,
): GetStartedTaskId[] {
  return GET_STARTED_TASKS_BY_INTENT[intent ?? WORKSPACE_INTENT.full];
}

export function trackOnboardingIntentSelected(options: {
  intent: WorkspaceIntent;
  mode: 'onboarding' | 'create';
}): void {
  captureEvent('onboarding_intent_selected', {
    intent: options.intent,
    mode: options.mode,
  });
  setPersonProperties({
    onboarding_intent: options.intent,
    onboarding_intent_mode: options.mode,
  });
}

const PENDING_INTENT_KEY = 'up2promo:pending_workspace_intent';

export function stashPendingWorkspaceIntent(intent: WorkspaceIntent): void {
  try {
    localStorage.setItem(PENDING_INTENT_KEY, intent);
  } catch {
    // ignore
  }
}

export function readPendingWorkspaceIntent(): WorkspaceIntent | null {
  try {
    const value = localStorage.getItem(PENDING_INTENT_KEY);
    return isWorkspaceIntent(value) ? value : null;
  } catch {
    return null;
  }
}

export function clearPendingWorkspaceIntent(): void {
  try {
    localStorage.removeItem(PENDING_INTENT_KEY);
  } catch {
    // ignore
  }
}
